import { useEffect } from "react";
import { LOCATIONS, SOCIAL } from "../data/site";

const ORIGIN = process.env.REACT_APP_SITE_ORIGIN || "https://guymezger.com";

export const videoSchema = ({ name, description, path, poster, uploadDate, duration }) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name,
  description,
  contentUrl: `${ORIGIN}${path}`,
  thumbnailUrl: [`${ORIGIN}${poster}`],
  uploadDate,
  ...(duration ? { duration } : {}),
  publisher: { "@type": "Organization", name: "Mezger Martial Arts" },
});

export const personSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${ORIGIN}/#guy-mezger`,
  name: "Guy Mezger",
  jobTitle: "Head Instructor",
  worksFor: { "@type": "Organization", name: "Hidden Gym" },
  description: "Former UFC champion and 3× King of Pancrase, World Kickboxing Champion, 6-time world champion across four full-contact sports, 7th degree black belt, and head instructor of Mezger Martial Arts at Hidden Gym with over 30 years of coaching in North Texas.",
  award: [
    "Ultimate Fighting Champion (UFC 13)",
    "3× King of Pancrase — World Free Fighting Champion",
    "World Kickboxing Champion",
    "2× World Full Contact Karate Champion",
    "WFFF World Freestyle Fighting Champion",
    "Texas Martial Arts Hall of Fame (2000)",
    "Masters Martial Arts Hall of Fame (2010)",
    "2001 Martial Arts Instructor of the Year",
  ],
  sameAs: [
    "https://en.wikipedia.org/wiki/Guy_Mezger",
    "https://www.sherdog.com/fighter/Guy-Mezger-118",
    "https://www.ufc.com/athlete/guy-mezger",
    SOCIAL.facebook,
    SOCIAL.instagram,
  ],
  url: `${ORIGIN}/about`,
});

export const orgSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${ORIGIN}/#organization`,
  name: "Mezger Martial Arts",
  url: ORIGIN,
  founder: { "@id": `${ORIGIN}/#guy-mezger` },
  parentOrganization: { "@type": "Organization", name: "Hidden Gym" },
  memberOf: { "@type": "Organization", name: "Hidden Gym" },
  sameAs: [SOCIAL.facebook, SOCIAL.instagram],
});

export const locationSchema = (loc) => ({
  "@context": "https://schema.org",
  "@type": ["SportsActivityLocation", "LocalBusiness"],
  "@id": `${ORIGIN}/locations/${loc.slug}#location`,
  name: loc.name,
  telephone: loc.phone,
  address: { "@type": "PostalAddress", streetAddress: loc.street, addressLocality: loc.city, addressRegion: "TX", postalCode: loc.zip, addressCountry: "US" },
  ...(loc.geo ? { geo: { "@type": "GeoCoordinates", latitude: loc.geo.lat, longitude: loc.geo.lng } } : {}),
  openingHoursSpecification: loc.hoursSpec.map((h) => ({
    "@type": "OpeningHoursSpecification",
    dayOfWeek: h.days,
    opens: h.opens,
    closes: h.closes,
  })),
  containedInPlace: { "@type": "SportsActivityLocation", name: "Hidden Gym", url: "https://hiddengym.net" },
  aggregateRating: { "@type": "AggregateRating", ratingValue: loc.rating.value, reviewCount: loc.rating.count },
  sameAs: [SOCIAL.facebook, SOCIAL.instagram],
  url: `${ORIGIN}/locations/${loc.slug}`,
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({
    "@type": "ListItem",
    position: i + 1,
    name,
    item: `${ORIGIN}${path}`,
  })),
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${ORIGIN}/#website`,
  name: "Mezger Martial Arts",
  url: ORIGIN,
  publisher: { "@id": `${ORIGIN}/#organization` },
});

export const webPageSchema = (name, description, path) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name,
  description,
  url: `${ORIGIN}${path}`,
  isPartOf: { "@id": `${ORIGIN}/#website` },
});

export const coachPersonSchema = (c) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${ORIGIN}/coaches/${c.slug}#person`,
  name: c.name,
  jobTitle: c.role,
  worksFor: { "@type": "Organization", name: "Mezger Martial Arts", parentOrganization: { "@type": "Organization", name: "Hidden Gym" } },
  description: c.bio,
  knowsAbout: c.disciplines,
  award: c.lineage,
  url: `${ORIGIN}/coaches/${c.slug}`,
});

export const coachListSchema = (coaches) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Mezger Martial Arts Coaching Staff",
  itemListElement: coaches.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: `${ORIGIN}/coaches/${c.slug}`,
    name: c.name,
  })),
});

export const programListSchema = (programs) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Martial Arts Programs at Mezger Martial Arts",
  itemListElement: programs.map((p, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: `${ORIGIN}/programs/${p.slug}`,
    name: p.name,
  })),
});

export const offerCatalogSchema = () => ({
  "@context": "https://schema.org",
  "@type": "OfferCatalog",
  name: "Hidden Gym Membership Options",
  provider: { "@type": "Organization", name: "Hidden Gym", url: "https://hiddengym.net" },
  itemListElement: [
    { "@type": "Offer", name: "Free Trial Class", price: "0", priceCurrency: "USD", description: "First martial arts class free at either Hidden Gym location." },
    { "@type": "Offer", name: "Monthly Unlimited Membership", description: "Unlimited Mezger Martial Arts classes plus full Hidden Gym facility access. Pricing set by Hidden Gym." },
    { "@type": "Offer", name: "Kids Membership", description: "Kids Combat Sports (7+) and Lion Cubs (5–6) programs. Pricing set by Hidden Gym." },
    { "@type": "Offer", name: "Private Lesson Packs", description: "One-on-one coaching with Guy Mezger or senior staff. Rates vary by coach." },
  ],
});

export const contactPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact Mezger Martial Arts",
  url: `${ORIGIN}/contact`,
  mainEntity: { "@id": `${ORIGIN}/#organization` },
});

export const blogSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Blog",
  name: "Mezger Martial Arts Blog",
  url: `${ORIGIN}/blog`,
  publisher: { "@id": `${ORIGIN}/#organization` },
});

export const courseSchema = (program) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  name: `${program.name} — Mezger Martial Arts at Hidden Gym`,
  description: program.what,
  provider: { "@id": `${ORIGIN}/#organization` },
  hasCourseInstance: program.locations.map((l) => ({
    "@type": "CourseInstance",
    courseMode: "onsite",
    location: { "@id": `${ORIGIN}/locations/${l}#location` },
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const hgHubSchema = () => ({
  "@context": "https://schema.org",
  "@type": "SportsActivityLocation",
  name: "Hidden Gym",
  department: {
    "@type": "SportsActivityLocation",
    name: "Mezger Martial Arts at Hidden Gym",
    employee: { "@type": "Person", name: "Guy Mezger", jobTitle: "Head Instructor", sameAs: [ORIGIN, "https://en.wikipedia.org/wiki/Guy_Mezger"] },
  },
  location: LOCATIONS.map((l) => ({ "@type": "Place", name: l.name, address: l.address, telephone: l.phone })),
  sameAs: [ORIGIN],
});

const DEFAULT_OG_IMAGE = `${ORIGIN}/media/photos/og-default.jpg`;

const setMetaProp = (attr, key, content) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
};

export default function SEO({ title, description, schemas = [], noindex = false, image = DEFAULT_OG_IMAGE, type = "website" }) {
  useEffect(() => {
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = description;

    setMetaProp("property", "og:title", title);
    setMetaProp("property", "og:description", description);
    setMetaProp("property", "og:image", image);
    setMetaProp("property", "og:type", type);
    setMetaProp("property", "og:url", `${ORIGIN}${window.location.pathname}`);
    setMetaProp("property", "og:site_name", "Mezger Martial Arts at Hidden Gym");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${ORIGIN}${window.location.pathname}`;
    setMetaProp("name", "twitter:card", "summary_large_image");
    setMetaProp("name", "twitter:title", title);
    setMetaProp("name", "twitter:description", description);
    setMetaProp("name", "twitter:image", image);

    let robots = document.querySelector('meta[name="robots"]');
    if (noindex) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.name = "robots";
        document.head.appendChild(robots);
      }
      robots.content = "noindex, nofollow";
    } else if (robots) {
      robots.remove();
    }

    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_title: title,
        page_location: window.location.href,
        page_path: window.location.pathname,
      });
    }

    document.querySelectorAll("script[data-seo-jsonld]").forEach((s) => s.remove());
    schemas.forEach((schema) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "1");
      s.textContent = JSON.stringify(schema);
      document.head.appendChild(s);
    });
    window.scrollTo(0, 0);
  }, [title, description, image, type]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}
